import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
    loadAttractionDetailDetailsByAttraction,
    loadAttractionDetails,
    loadDeliveredLanguageDetails,
    loadDriversLicenceDetails,
    loadEthnicityDetails,
    loadFirstLanguageDetails,
    loadHousingDetails,
    loadMaritalStatusDetails
} from '../../../store/directusService';
import {
    clearParticipantGeneralInformation,
    clearValidationErrors,
    loadContractConstants,
    loadParticipantGeneralInformation,
    updateParticipantGeneralInformation
} from '../../../store/participantService';
import { getConfiguredItems } from '../../../utils/directusFunctions';
import { EDIT_ON_CLOSED_ROLES, MAX_EMAIL } from '../../../utils/formValidation/commonConstants';
import {
    ATTRACTION_DETAILS_ERROR,
    ETHNICITY_DETAILS_ERROR,
    HOUSING_DETAILS_ERROR,
    LANGUAGE_DETAILS_ERROR,
    LICENCE_DETAILS_ERROR,
    MARITAL_STATUS_DETAILS_ERROR
} from '../../../utils/formValidation/loadingErrorMessageConstants';
import { regExps } from '../../../utils/formValidation/validationPatterns';
import { clearKeys } from '../../../utils/objectUtils';
import {
    ADVISER,
    hasRole,
    MANAGER,
    PRAP,
    QUALITY,
    RECRUITMENT_MANAGER,
    SUPERUSER
} from '../../../utils/userRoles';
import Button from '../../formElements/Button';
import MultiSelect from '../../formElements/MultiSelect';
import RadioButtons from '../../formElements/RadioButtons';
import SingleSelect from '../../formElements/SingleSelect';
import TextInputField from '../../formElements/TextInputField';
import LoadingSpinner from '../../ui/LoadingSpinner';
import PillList from '../../ui/pillList/PillList';

import { MAX_OTHER_LANGUAGE, validate } from './validateParticipantInformation';

import form from '../../../commonStyles/formStyles.module.css';

const RADIO_BUTTON_NO = 'no';
const RADIO_BUTTON_YES = 'yes';
const RADIO_BUTTON_PREFER_NOT_TO_SAY = 'prefer not to say';
const _PO_NUMBER_MAX_CHAR = 100;

const acceptedRoles = [ADVISER, MANAGER, PRAP, QUALITY, RECRUITMENT_MANAGER, SUPERUSER];
const acceptedRolesForReferringOfficer = [
    ADVISER,
    MANAGER,
    QUALITY,
    RECRUITMENT_MANAGER,
    SUPERUSER
];
const acceptedRolesForPoNumberExpanded = [ADVISER, MANAGER, PRAP, QUALITY, SUPERUSER];
const acceptedRolesForPoNumberBasic = [PRAP, QUALITY, SUPERUSER];

const ParticipantInformation = () => {
    // HOOKS
    const dispatch = useDispatch();

    // LOCAL STATE
    const initialState = {
        accessToOwnTransport: null,
        accessToPublicTransport: null,
        attractionId: null,
        attractionDetailId: null,
        hasDependents: 'no',
        deliveredLanguageId: null,
        dependents: [],
        driversLicenseIds: [],
        ethnicityId: null,
        firstLanguageId: null,
        hobbies: [],
        housingId: null,
        housingRegistrationCheck: false,
        maritalStatusId: null,
        otherFirstLanguage: null,
        otherDeliveredLanguage: null,
        poNumber: '',
        externalReferenceNumber: '',
        referringOfficerName: '',
        referringOfficerEmailAddress: ''
    };

    const [newEntry, setNewEntry] = useState(initialState);
    const [initialStateOnEntry, setInitialStateOnEntry] = useState(initialState);
    const [errors, setErrors] = useState({});
    const [preSelectedDriverLicenses, setPreSelectedDriverLicenses] = useState([]);
    const [preSelectedDriverLicenseIds, setPreSelectedDriverLicenseIds] = useState([]);

    const [arrayEthnicities, setArrayEthnicities] = useState([]);
    const [arrayFirstLanguages, setArrayFirstLanguages] = useState([]);
    const [arrayDriversLicences, setArrayDriversLicences] = useState([]);
    const [arrayMaritalStatuses, setArrayMaritalStatuses] = useState([]);
    const [arrayHousing, setArrayHousing] = useState([]);
    const [arrayAttraction, setArrayAttraction] = useState([]);
    const [arrayAttractionDetail, setArrayAttractionDetail] = useState([]);

    const [dependentsTextContent, setDependentsTextContent] = useState('');
    const [hobbiesTextContent, setHobbiesTextContent] = useState('');
    const [showHobbiesTextField, setShowHobbiesTextField] = useState(false);
    const [isUpdateButtonDisabled, setIsUpdateButtonDisabled] = useState(false);

    const [isDisabled, setIsDisabled] = useState(true);
    const [isDisabledReferringOfficer, setIsDisabledReferringOfficer] = useState(true);
    const [isDisabledPoNumber, setIsDisabledPoNumber] = useState(true);

    const [otherFirstLanguageId, setOtherFirstLanguageId] = useState('');
    const [otherDeliveredLanguageId, setOtherDeliveredLanguageId] = useState('');

    const [keys, setKeys] = useState({
        ethnicity: '0',
        firstLanguage: '1',
        driversLicences: '2',
        maritalStatus: '3',
        housing: '4',
        attraction: '5',
        attractionDetail: '6'
    });

    // STORE STATE
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const currentParticipantGeneralInformation = useSelector(
        (state) => state.entities.participantService.currentParticipantGeneralInformation
    );
    const contractConstants = useSelector(
        (state) => state.entities.participantService.contractConstants
    );
    const firstLanguageDetails = useSelector(
        (state) => state.entities.directusService.firstLanguageDetails
    );
    const deliveredLanguageDetails = useSelector(
        (state) => state.entities.directusService.deliveredLanguageDetails
    );
    const driversLicenceDetails = useSelector(
        (state) => state.entities.directusService.driversLicenceDetails
    );
    const ethnicityDetails = useSelector(
        (state) => state.entities.directusService.ethnicityDetails
    );
    const maritalStatusDetails = useSelector(
        (state) => state.entities.directusService.maritalStatusDetails
    );
    const housingDetails = useSelector((state) => state.entities.directusService.housingDetails);
    const attractionDetails = useSelector(
        (state) => state.entities.directusService.attractionDetails
    );
    const attractionDetailDetails = useSelector(
        (state) => state.entities.directusService.attractionDetailDetails
    );
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);
    const validationErrors = useSelector(
        (state) => state.entities.participantService.validationErrors
    );

    // USE EFFECTS
    useEffect(() => {
        dispatch(loadEthnicityDetails());
        dispatch(loadFirstLanguageDetails());
        dispatch(loadDriversLicenceDetails());
        dispatch(loadMaritalStatusDetails());
        dispatch(loadHousingDetails());
        dispatch(loadAttractionDetails());
        !deliveredLanguageDetails?.length && dispatch(loadDeliveredLanguageDetails());
        return () => cleanUp();
    }, []);

    useEffect(() => {
        setKeys(clearKeys(keys));
        setNewEntry(initialState);
        setInitialStateOnEntry(initialState);
        if (currentParticipant?.id) {
            dispatch(loadParticipantGeneralInformation(currentParticipant.id));
            dispatch(loadContractConstants(currentParticipant.contractId));
        }
    }, [currentParticipant.id]);

    useEffect(() => {
        if (
            !contractConstants?.contractId === currentParticipant.contractId ||
            !currentParticipant?.id
        )
            return;
        const acceptedRolesForPoNumber = contractConstants.expandPoNumberAccessPermissions
            ? acceptedRolesForPoNumberExpanded
            : acceptedRolesForPoNumberBasic;

        const isDisabledOnClosedStatus =
            currentParticipant.status === 'Closed' && !hasRole(EDIT_ON_CLOSED_ROLES, roles);

        setIsDisabled(!hasRole(acceptedRoles, roles) || isDisabledOnClosedStatus);
        setIsDisabledReferringOfficer(
            !hasRole(acceptedRolesForReferringOfficer, roles) || isDisabledOnClosedStatus
        );
        setIsDisabledPoNumber(
            !hasRole(acceptedRolesForPoNumber, roles) || isDisabledOnClosedStatus
        );
    }, [currentParticipant.id, currentParticipant.status, contractConstants]);

    useEffect(() => {
        if (currentParticipantGeneralInformation?.participantId === currentParticipant?.id) {
            if (currentParticipantGeneralInformation.attractionId) {
                dispatch(
                    loadAttractionDetailDetailsByAttraction(
                        currentParticipantGeneralInformation.attractionId
                    )
                );
            }
            setPreSelectedDriverLicenseIds(currentParticipantGeneralInformation.driversLicenseIds);
            setNewEntry((prev) => ({ ...prev, ...currentParticipantGeneralInformation }));
            setInitialStateOnEntry((prev) => ({
                ...prev,
                ...currentParticipantGeneralInformation
            }));
            setShowHobbiesTextField(
                currentParticipantGeneralInformation.hobbies &&
                    currentParticipantGeneralInformation.hobbies.length > 0
            );
        }
    }, [currentParticipantGeneralInformation.participantId]);

    useEffect(() => {
        setArrayEthnicities(
            getConfiguredItems(ethnicityDetails, currentParticipant?.contractId) || []
        );
    }, [ethnicityDetails]);

    useEffect(() => {
        setArrayFirstLanguages(
            getConfiguredItems(firstLanguageDetails, currentParticipant?.contractId) || []
        );
    }, [firstLanguageDetails]);

    useEffect(() => {
        if (!arrayFirstLanguages?.length) return;

        const otherId = arrayFirstLanguages.find((el) => el.name.toLowerCase() === 'other');
        otherId && setOtherFirstLanguageId(otherId.id);
    }, [arrayFirstLanguages]);

    useEffect(() => {
        if (!deliveredLanguageDetails?.length) return;

        const otherId = deliveredLanguageDetails.find((el) => el.name.toLowerCase() === 'other');
        otherId && setOtherDeliveredLanguageId(otherId.id);
    }, [deliveredLanguageDetails]);

    useEffect(() => {
        setArrayDriversLicences(
            getConfiguredItems(driversLicenceDetails, currentParticipant?.contractId) || []
        );
    }, [driversLicenceDetails]);

    useEffect(() => {
        if (
            currentParticipantGeneralInformation?.participantId === currentParticipant?.id &&
            arrayDriversLicences?.length !== 0
        ) {
            const preSelectedDriverLicenses = arrayDriversLicences.filter((el) =>
                currentParticipantGeneralInformation.driversLicenseIds?.includes(el.id)
            );
            setPreSelectedDriverLicenses(preSelectedDriverLicenses);
        }
    }, [arrayDriversLicences, currentParticipantGeneralInformation.participantId]);

    useEffect(() => {
        setArrayMaritalStatuses(
            getConfiguredItems(maritalStatusDetails, currentParticipant?.contractId) || []
        );
    }, [maritalStatusDetails]);

    useEffect(() => {
        setArrayHousing(getConfiguredItems(housingDetails, currentParticipant?.contractId) || []);
    }, [housingDetails]);

    useEffect(() => {
        setArrayAttraction(
            getConfiguredItems(attractionDetails, currentParticipant?.contractId) || []
        );
    }, [attractionDetails]);

    useEffect(() => {
        setArrayAttractionDetail(attractionDetailDetails);
    }, [attractionDetailDetails]);

    useEffect(() => {
        if (successMessage === 'Participant general details have been updated') {
            setIsUpdateButtonDisabled(false);
            setNewEntry(currentParticipantGeneralInformation);
            setInitialStateOnEntry(currentParticipantGeneralInformation);
        }
        if (validationErrors && Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            dispatch(clearValidationErrors());
        }
    }, [successMessage, validationErrors]);

    useEffect(() => {
        setIsUpdateButtonDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    // HELPER FNS
    const loadingError = () => {
        if (arrayEthnicities?.length < 1) return ETHNICITY_DETAILS_ERROR;
        if (arrayFirstLanguages?.length < 1) return LANGUAGE_DETAILS_ERROR;
        if (arrayDriversLicences?.length < 1) return LICENCE_DETAILS_ERROR;
        if (arrayMaritalStatuses?.length < 1) return MARITAL_STATUS_DETAILS_ERROR;
        if (arrayHousing?.length < 1) return HOUSING_DETAILS_ERROR;
        if (arrayAttraction?.length < 1) return ATTRACTION_DETAILS_ERROR;
    };
    const clearError = (key) => {
        if (key in errors && errors[key].error)
            setErrors((prev) => ({ ...prev, [key]: { error: false } }));
    };

    const cleanUp = () => dispatch(clearParticipantGeneralInformation());

    // EVENT HANDLERS
    const onAttractionChange = (chosenId) => {
        onUpdate('attractionId', chosenId);
        if (chosenId) {
            setArrayAttractionDetail([]);
            dispatch(loadAttractionDetailDetailsByAttraction(chosenId));
        }
    };

    const onUpdateHasDependents = (option) => {
        onUpdate('hasDependents', option);
        if (option !== RADIO_BUTTON_YES) {
            onUpdate('dependents', []);
        }
    };

    const onAddingDependent = () => {
        if (
            dependentsTextContent.length === 0 ||
            newEntry.dependents.includes(dependentsTextContent)
        )
            return;
        onUpdate('dependents', [...newEntry.dependents, dependentsTextContent]);
        setDependentsTextContent('');
    };

    const onRemovingDependent = (dependentToBeRemoved) => {
        onUpdate(
            'dependents',
            newEntry.dependents.filter((dependent) => dependent !== dependentToBeRemoved)
        );
    };

    const onAddingHobby = () => {
        if (hobbiesTextContent.length === 0 || newEntry.hobbies.includes(hobbiesTextContent))
            return;
        onUpdate('hobbies', [...newEntry.hobbies, hobbiesTextContent]);
        setHobbiesTextContent('');
    };

    const onRemovingHobby = (hobbyToBeRemoved) => {
        onUpdate(
            'hobbies',
            newEntry.hobbies.filter((hobby) => hobby !== hobbyToBeRemoved)
        );
    };

    const onUpdateDeliveredLanguage = (chosenId) => {
        if (newEntry.deliveredLanguageId === otherDeliveredLanguageId)
            onUpdate('otherDeliveredLanguage', '');
        onUpdate('deliveredLanguageId', chosenId);
    };

    const onUpdateFirstLanguage = (chosenId) => {
        if (newEntry.firstLanguageId === otherFirstLanguageId) onUpdate('otherFirstLanguage', '');
        onUpdate('firstLanguageId', chosenId);
    };

    const onUpdate = (key, value) => {
        clearError(key);
        setNewEntry((prev) => ({ ...prev, [key]: value }));
    };

    const onUpdateReferringOfficerEmailAddress = (value) => {
        onUpdate('referringOfficerEmailAddress', value);
    };

    const onUpdatePONumber = (e) => {
        clearError('poNumber');
        setNewEntry((prev) => ({
            ...prev,
            poNumber: e.target.value.replace(regExps.PO_NUMBER, '')
        }));
    };

    const onSubmit = (e) => {
        e.preventDefault();
        setIsUpdateButtonDisabled(true);
        const { newErrors } = validate(
            newEntry,
            initialStateOnEntry,
            otherFirstLanguageId,
            otherDeliveredLanguageId
        );
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }
        const payload = {
            ...newEntry,
            participantId: currentParticipant.id,
            attractionDetailId: newEntry.attractionId === null ? null : newEntry.attractionDetailId
        };
        dispatch(updateParticipantGeneralInformation(payload));
    };

    // AWAITING CONTENT
    const errorMsg = loadingError();
    if (errorMsg) return <LoadingSpinner content={errorMsg} />;

    // RENDER
    return (
        <div className={form.formWrapper}>
            <form onSubmit={onSubmit} data-testid="form_start" className={form.form}>
                <div className={form.formSection}>
                    <div className={form.formColumn}>
                        <SingleSelect
                            id={'housingId'}
                            key={keys.housing}
                            label={'Housing'}
                            placeholder={'Search housing...'}
                            disabled={isDisabled}
                            menuItems={arrayHousing}
                            selectedId={newEntry.housingId}
                            selected={arrayHousing.find((el) => el.id === newEntry.housingId) || {}}
                            onChange={(chosenId) => onUpdate('housingId', chosenId)}
                        />
                        <SingleSelect
                            id={'attractionId'}
                            key={keys.attraction}
                            label={'Attraction'}
                            placeholder={'Search attraction...'}
                            disabled={isDisabled}
                            menuItems={arrayAttraction}
                            selectedId={newEntry.attractionId}
                            selected={
                                arrayAttraction.find((el) => el.id === newEntry.attractionId) || {}
                            }
                            onChange={(chosenId) => onAttractionChange(chosenId)}
                        />
                        <SingleSelect
                            id={'attractionDetailId'}
                            key={keys.attractionDetail}
                            label={'Attraction Detail'}
                            placeholder={'Search attraction details...'}
                            disabled={!newEntry.attractionId || isDisabled}
                            menuItems={arrayAttractionDetail}
                            selectedId={newEntry.attractionDetailId}
                            selected={
                                arrayAttractionDetail?.find(
                                    (el) => el.id === newEntry.attractionDetailId
                                ) || {}
                            }
                            onChange={(chosenId) => onUpdate('attractionDetailId', chosenId)}
                        />
                        <MultiSelect
                            id="driversLicences"
                            key={keys.driversLicences}
                            label="Driver's Licence"
                            disabled={isDisabled}
                            placeholder="Drivers Licence"
                            menuItems={arrayDriversLicences}
                            preSelectedIds={preSelectedDriverLicenseIds}
                            preSelects={preSelectedDriverLicenses}
                            onChange={(chosenIds) => onUpdate('driversLicenseIds', chosenIds)}
                        />
                        <RadioButtons
                            id={'accessToOwnTransport'}
                            label={'Access to own transport'}
                            disabled={isDisabled}
                            value={newEntry.accessToOwnTransport || false}
                            onChange={(option) => onUpdate('accessToOwnTransport', option)}
                        />
                        <RadioButtons
                            id={'accessToPublicTransport'}
                            label={'Access to Public transport'}
                            disabled={isDisabled}
                            value={newEntry.accessToPublicTransport || false}
                            onChange={(option) => onUpdate('accessToPublicTransport', option)}
                        />
                        <RadioButtons
                            id={'housingRegChecked'}
                            label={'Housing Registration Check'}
                            disabled={isDisabled}
                            value={newEntry.housingRegistrationCheck || false}
                            onChange={(option) => onUpdate('housingRegistrationCheck', option)}
                        />
                        <SingleSelect
                            id={'ethnicityId'}
                            key={keys.ethnicity}
                            label={'Ethnicity'}
                            placeholder={'Search ethnicities...'}
                            disabled={isDisabled}
                            menuItems={arrayEthnicities}
                            selectedId={newEntry.ethnicityId}
                            selected={
                                arrayEthnicities.find((el) => el.id === newEntry.ethnicityId) || {}
                            }
                            onChange={(chosenId) => onUpdate('ethnicityId', chosenId)}
                        />
                        <SingleSelect
                            id={'deliveredLanguageId'}
                            label={'Delivered Language'}
                            placeholder={'Search delivered languages...'}
                            disabled={isDisabled}
                            menuItems={deliveredLanguageDetails}
                            selectedId={newEntry.deliveredLanguageId}
                            selected={
                                deliveredLanguageDetails.find(
                                    (el) => el.id === newEntry.deliveredLanguageId
                                ) || {}
                            }
                            onChange={(chosenId) => onUpdateDeliveredLanguage(chosenId)}
                        />
                        {otherDeliveredLanguageId === newEntry.deliveredLanguageId && (
                            <TextInputField
                                id={'otherDeliveredLanguage'}
                                label={'Other Delivered Language'}
                                placeholder={'Enter other delivered language...'}
                                disabled={isDisabled}
                                mandatory={true}
                                maxLength={MAX_OTHER_LANGUAGE}
                                value={newEntry.otherDeliveredLanguage || ''}
                                error={errors.otherDeliveredLanguage}
                                onChange={(e) => onUpdate('otherDeliveredLanguage', e.target.value)}
                            />
                        )}
                    </div>
                    <div className={form.formColumn}>
                        <SingleSelect
                            id={'firstLanguageId'}
                            key={keys.firstLanguage}
                            label={'First Language'}
                            placeholder={'Search first languages...'}
                            disabled={isDisabled}
                            menuItems={arrayFirstLanguages}
                            selectedId={newEntry.firstLanguageId}
                            selected={
                                arrayFirstLanguages.find(
                                    (el) => el.id === newEntry.firstLanguageId
                                ) || {}
                            }
                            onChange={(chosenId) => onUpdateFirstLanguage(chosenId)}
                        />
                        {otherFirstLanguageId === newEntry.firstLanguageId && (
                            <TextInputField
                                id={'otherFirstLanguage'}
                                label={'Other First Language'}
                                placeholder={'Enter other first language'}
                                disabled={isDisabled}
                                mandatory={true}
                                maxLength={MAX_OTHER_LANGUAGE}
                                value={newEntry.otherFirstLanguage || ''}
                                error={errors.otherFirstLanguage}
                                onChange={(e) => onUpdate('otherFirstLanguage', e.target.value)}
                            />
                        )}
                        <SingleSelect
                            id={'maritalStatusId'}
                            key={keys.maritalStatus}
                            label={'Marital Status'}
                            placeholder={'Search marital statuses...'}
                            disabled={isDisabled}
                            menuItems={arrayMaritalStatuses}
                            selectedId={newEntry.maritalStatusId}
                            selected={
                                arrayMaritalStatuses.find(
                                    (el) => el.id === newEntry.maritalStatusId
                                ) || {}
                            }
                            onChange={(chosenId) => onUpdate('maritalStatusId', chosenId)}
                        />
                        <RadioButtons
                            id={'hasDependents'}
                            disabled={isDisabled}
                            label={'Dependents'}
                            radioButtons={[
                                { id: RADIO_BUTTON_NO, label: 'No', onChangeVal: RADIO_BUTTON_NO },
                                {
                                    id: RADIO_BUTTON_YES,
                                    label: 'Yes',
                                    onChangeVal: RADIO_BUTTON_YES
                                },
                                {
                                    id: RADIO_BUTTON_PREFER_NOT_TO_SAY,
                                    label: 'Prefer not to say',
                                    onChangeVal: RADIO_BUTTON_PREFER_NOT_TO_SAY
                                }
                            ]}
                            value={newEntry.hasDependents}
                            onChange={onUpdateHasDependents}
                        />
                        <PillList
                            id={'dependent-pills'}
                            disabled={isDisabled}
                            pills={newEntry.dependents}
                            onRemoving={onRemovingDependent}
                        />
                        {newEntry.hasDependents === RADIO_BUTTON_YES && (
                            <TextInputField
                                id={'dependents'}
                                label={"Enter Dependant's Name"}
                                disabled={isDisabled}
                                value={dependentsTextContent}
                                onChange={(e) => setDependentsTextContent(e.target.value)}
                                onEnter={onAddingDependent}
                            />
                        )}
                        <RadioButtons
                            id={'hobbiesRadio'}
                            disabled={isDisabled}
                            label={'Hobbies and Interests'}
                            value={showHobbiesTextField}
                            onChange={(option) => setShowHobbiesTextField(option)}
                        />
                        <PillList
                            id={'hobby-pills'}
                            disabled={isDisabled}
                            pills={newEntry.hobbies}
                            onRemoving={onRemovingHobby}
                        />
                        {showHobbiesTextField && (
                            <TextInputField
                                id={'hobbiesTextField'}
                                label={'Enter Hobby or Interests'}
                                disabled={isDisabled}
                                value={hobbiesTextContent}
                                onChange={(e) => setHobbiesTextContent(e.target.value)}
                                onEnter={onAddingHobby}
                            />
                        )}
                        <TextInputField
                            id={'poNumber'}
                            label={'PO Number'}
                            placeholder={'Enter value'}
                            disabled={isDisabledPoNumber}
                            maxLength={_PO_NUMBER_MAX_CHAR}
                            value={newEntry.poNumber || ''}
                            error={errors.poNumber}
                            onChange={onUpdatePONumber}
                        />
                        {!!contractConstants.useExternalReferenceNumber && (
                            <TextInputField
                                id={'externalReferenceNumber'}
                                label={'External Reference Number'}
                                placeholder={'Enter external reference number...'}
                                disabled={isDisabled}
                                maxLength={50}
                                value={newEntry.externalReferenceNumber || ''}
                                onChange={(e) =>
                                    setNewEntry((prev) => ({
                                        ...prev,
                                        externalReferenceNumber: e.target.value.replace(
                                            regExps.EXTERNAL_REFERENCE_NUMBER_R,
                                            ''
                                        )
                                    }))
                                }
                            />
                        )}
                        <TextInputField
                            id={'referringOfficerName'}
                            label={'Referring Officer Name'}
                            placeholder={'Enter referring officer name...'}
                            disabled={isDisabledReferringOfficer}
                            maxLength={200}
                            value={newEntry.referringOfficerName || ''}
                            onChange={(e) => onUpdate('referringOfficerName', e.target.value)}
                        />
                        <TextInputField
                            id={'referringOfficerEmailAddress'}
                            label={'Referring Officer Email Address'}
                            placeholder={'Enter referring officer email address...'}
                            disabled={isDisabledReferringOfficer}
                            maxLength={MAX_EMAIL}
                            value={newEntry.referringOfficerEmailAddress || ''}
                            error={errors.referringOfficerEmailAddress}
                            onChange={(e) => onUpdateReferringOfficerEmailAddress(e.target.value)}
                        />
                    </div>
                </div>

                <Button
                    data-testid="testIdSubmitButton"
                    content={'Update Participant Information'}
                    disabled={isDisabled || isUpdateButtonDisabled}
                    error={errors.button}
                    clearError={() => clearError('button')}
                />
            </form>
        </div>
    );
};

export default ParticipantInformation;
